import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';

import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';

import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';

import { formatDate } from '@lib/commons/util-functions/format-date.function';

import { DocumentIconComponent } from '@lib/medical-documents/components/document-icon/document-icon.component';
import { editDocumentDetails, openDocumentDetails, setCurrentAiDocumentDetails } from '@lib/medical-documents/core/store/medical-documents.actions';
import { selectCurrentAiDocument } from '@lib/medical-documents/core/store/medical-documents.selectors';

import { AiMedicalDocumentItem, AppointmentType, DocumentProcessingStatus } from '../../../../core/models/document.interface';

@Component({
  selector: 'md-ai-document-item',
  standalone: true,
  imports: [NgClass, MatIcon, DocumentIconComponent, MatMenu, MatMenuItem, MatMenuTrigger, TranslatePipe],
  templateUrl: './ai-list-item.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiListItemComponent {
  private readonly store = inject(Store);

  aiDocument = input.required<AiMedicalDocumentItem>();
  documentDeleted = output<void>();

  protected readonly documentType = AppointmentType;
  protected readonly formatDate = formatDate;

  protected readonly currentDocument = this.store.selectSignal<AiMedicalDocumentItem>(selectCurrentAiDocument);

  protected readonly selected = computed<boolean>(() => {
    if (this.currentDocument() === null) return false;
    return this.aiDocument().id === this.currentDocument().id;
  });
  protected readonly isUploading = computed<boolean>(() => {
    return this.aiDocument().isDraft && this.aiDocument().processingStatus === DocumentProcessingStatus.InProgress;
  });
  protected readonly isAnalyzing = computed<boolean>(() => {
    return !this.aiDocument().isDraft && this.aiDocument().processingStatus === DocumentProcessingStatus.InProgress;
  });

  protected selectDocument() {
    if (!this.selected()) {
      this.store.dispatch(setCurrentAiDocumentDetails({ currentAiDocument: this.aiDocument() }));
    }
  }

  protected openDetails() {
    this.store.dispatch(openDocumentDetails({ document: this.aiDocument() }));
    this.store.dispatch(editDocumentDetails());
  }

  protected deleteMedicalDocument() {
    this.documentDeleted.emit();
  }
}
